import { Box, Button, Chip, CircularProgress, InputAdornment, LinearProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { RightPanelContext } from './entityContextPanel';
import { EntityType } from '@/components/entities/entityType';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AppsIcon from '@mui/icons-material/Apps';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StorageIcon from '@mui/icons-material/Storage';

type Props = {
  rightPanelContext: RightPanelContext;
  onPipelineSelect: (pipelineId: string) => void;
  onEntitySelect: (entityInfo: any) => void;
};

const getCategoryLabel = (type?: string): string => {
  switch (type) {
    case 'data_source':
    case 'dataSourceInstance':
    case 'dataSourceDef':
    case 'dataSourceItem':
    case 'dataSourceItemGroup':
      return 'Data Sources';
    case 'agent':
    case 'agentInstance':
    case 'agentDef':
      return 'Agents';
    case 'pipeline':
    case 'pipelineInstance':
    case 'pipelineBlueprint':
      return 'Pipelines';
    case 'user_app':
    case 'userAppInstance':
    case 'userAppDef':
      return 'User Apps';
    default:
      return 'Components';
  }
};

const EntityListPanel = ({ rightPanelContext, onPipelineSelect, onEntitySelect }: Props) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingItemId, setLoadingItemId] = useState<string | null>(null);

  // Clear loadingItemId if the right panel context changes
  useEffect(() => {
    setLoadingItemId(null);
  }, [rightPanelContext]);

  const title = rightPanelContext.entityList?.title || '';
  const entityListItems = rightPanelContext.entityList?.items || [];
  const availableTypes = useMemo(() => {
    const set = new Set<string>();
    entityListItems.forEach((it: any) => {
      if (it.entityType) set.add(it.entityType);
    });
    return Array.from(set);
  }, [entityListItems]);

  const filteredListItems = useMemo(() => {
    let list = entityListItems;
    if (selectedCategory && selectedCategory !== 'all') {
      list = list.filter((it: any) => it.entityType === selectedCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((it: any) => {
        const name = (it.name || it.pipelineName || it.id || '').toLowerCase();
        const desc = (it.description || '').toLowerCase();
        return name.includes(q) || desc.includes(q);
      });
    }
    return list;
  }, [entityListItems, selectedCategory, searchQuery]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden', position: 'relative' }}>
      {loadingItemId && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 2.5,
            zIndex: 10,
          }}
        />
      )}
      <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider', bgcolor: '#ffffff', flexShrink: 0 }}>
        {availableTypes.length > 1 && (
          <Stack direction="row" spacing={1} sx={{ mb: 1.25 }} flexWrap="wrap">
            <Chip
              label={`All (${entityListItems.length})`}
              size="small"
              variant={selectedCategory === 'all' ? 'filled' : 'outlined'}
              color={selectedCategory === 'all' ? 'primary' : 'default'}
              onClick={() => setSelectedCategory('all')}
              sx={{ fontSize: '0.7rem', cursor: 'pointer', mb: 0.5 }}
            />
            {availableTypes.map((type) => {
              const count = entityListItems.filter((it: any) => it.entityType === type).length;
              return (
                <Chip
                  key={type}
                  label={`${getCategoryLabel(type)} (${count})`}
                  size="small"
                  variant={selectedCategory === type ? 'filled' : 'outlined'}
                  color={selectedCategory === type ? 'primary' : 'default'}
                  onClick={() => setSelectedCategory(type)}
                  sx={{ fontSize: '0.7rem', cursor: 'pointer', mb: 0.5 }}
                />
              );
            })}
          </Stack>
        )}
        <TextField
          fullWidth
          size="small"
          placeholder={`Filter ${title.toLowerCase()}...`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
            sx: { fontSize: '0.8rem', height: 32 },
          }}
        />
      </Box>
      <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', p: 1.5, bgcolor: '#f8fafc' }}>
        {filteredListItems.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No matching components found.
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {filteredListItems.map((item: any) => {
              const itemId = item.id || item.pipelineId || item.dataSourceDefId || item.agentDefId;
              const itemName = item.name || item.pipelineName || itemId;
              const itemType = item.entityType || 'pipeline';
              const isDataSource =
                itemType === 'data_source' ||
                itemType === EntityType.dataSourceInstance ||
                itemType === EntityType.dataSourceDef;
              const isAgent =
                itemType === 'agent' || itemType === EntityType.agentInstance || itemType === EntityType.agentDef;
              const isPipeline =
                itemType === 'pipeline' ||
                itemType === EntityType.pipelineInstance ||
                itemType === EntityType.pipelineBlueprint;
              const isUserApp =
                itemType === 'user_app' ||
                itemType === EntityType.userAppInstance ||
                itemType === EntityType.userAppDef;
              const description = item.description || item.metadata?.description;
              const stepCount = item.entityCount || item.entities?.length || item.steps?.length;
              const isLoading = loadingItemId === itemId;

              return (
                <Paper
                  key={itemId}
                  variant="outlined"
                  onClick={() => {
                    if (loadingItemId) return;
                    setLoadingItemId(itemId);
                    if (isPipeline) {
                      onPipelineSelect(itemId);
                    } else if (onEntitySelect) {
                      const resolvedType = isDataSource
                        ? EntityType.dataSourceInstance
                        : isAgent
                          ? EntityType.agentInstance
                          : isUserApp
                            ? EntityType.userAppInstance
                            : (item.entityType as EntityType) || EntityType.dataSourceInstance;
                      onEntitySelect({
                        type: 'single',
                        entityType: resolvedType,
                        id: itemId,
                        name: itemName,
                        data: item,
                        previousContext: rightPanelContext,
                      });
                    }
                  }}
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: isLoading ? '#f0f9ff' : '#ffffff',
                    borderColor: isLoading ? '#3b82f6' : undefined,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease-in-out',
                    '&:hover': { borderColor: 'primary.main', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap">
                        {isDataSource && <StorageIcon sx={{ fontSize: 16, color: '#0284c7' }} />}
                        {isAgent && <SmartToyIcon sx={{ fontSize: 16, color: '#8b5cf6' }} />}
                        {isPipeline && <AccountTreeIcon sx={{ fontSize: 16, color: '#10b981' }} />}
                        {isUserApp && <AppsIcon sx={{ fontSize: 16, color: '#f59e0b' }} />}
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                          {itemName}
                        </Typography>
                        <Chip
                          label={getCategoryLabel(itemType)}
                          size="small"
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600 }}
                        />
                        {stepCount !== undefined && (
                          <Chip
                            label={`${stepCount} step(s)`}
                            size="small"
                            sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#f1f5f9', color: '#475569' }}
                          />
                        )}
                        {item.active !== undefined && (
                          <Chip
                            label={item.active !== false ? 'Active' : 'Inactive'}
                            size="small"
                            color={item.active !== false ? 'success' : 'default'}
                            sx={{ height: 18, fontSize: '0.6rem' }}
                          />
                        )}
                      </Stack>
                      {description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: 1.3,
                          }}
                        >
                          {description}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant={isLoading ? 'outlined' : 'contained'}
                      size="small"
                      disabled={loadingItemId !== null && !isLoading}
                      startIcon={isLoading ? <CircularProgress size={12} thickness={5} color="primary" /> : undefined}
                      endIcon={!isLoading ? <OpenInNewIcon sx={{ fontSize: 14 }} /> : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (loadingItemId) return;
                        setLoadingItemId(itemId);
                        if (isPipeline) {
                          onPipelineSelect(itemId);
                        } else if (onEntitySelect) {
                          const resolvedType = isDataSource
                            ? EntityType.dataSourceInstance
                            : isAgent
                              ? EntityType.agentInstance
                              : isUserApp
                                ? EntityType.userAppInstance
                                : (item.entityType as EntityType) || EntityType.dataSourceInstance;
                          onEntitySelect({
                            type: 'single',
                            entityType: resolvedType,
                            id: itemId,
                            name: itemName,
                            data: item,
                            previousContext: rightPanelContext,
                          });
                        }
                      }}
                      sx={{ fontSize: '0.72rem', py: 0.3, px: 1.25, flexShrink: 0, textTransform: 'none' }}
                    >
                      {isLoading ? 'Loading...' : isPipeline ? 'Open' : 'Inspect'}
                    </Button>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default EntityListPanel;
